import { type CategoryResponse, formatEUR, formatSignedEUR, splitEvenly } from '@budget/shared';
import { useState } from 'react';
import { AmountField } from './AmountField';
import { BottomSheet } from './BottomSheet';

interface Props {
  totalCents: number;
  categories: CategoryResponse[];
  /** Rend les parts dans l'ordre des catégories reçues. */
  onConfirm: (parts: number[]) => void;
  onClose: () => void;
  pending: boolean;
  error: string | null;
}

/**
 * Ventilation d'une dépense entre les catégories choisies.
 *
 * Les parts sont d'abord réparties équitablement, au centime près : 10 € en
 * trois donnent 3,34 € + 3,33 € + 3,33 €, jamais trois fois 3,33 € qui
 * perdraient un centime en route.
 *
 * Le reste à répartir est affiché en permanence et bloque l'enregistrement
 * tant qu'il n'est pas nul. Le total vient d'un ticket : il ne se négocie pas.
 * On préfère le dire plutôt que de rattraper l'écart en silence sur la
 * dernière part, ce qui surprendrait au moment de relire.
 */
export function SplitModal({
  totalCents,
  categories,
  onConfirm,
  onClose,
  pending,
  error,
}: Props) {
  const [parts, setParts] = useState<number[]>(() => splitEvenly(totalCents, categories.length));

  const assigned = parts.reduce((sum, part) => sum + part, 0);
  const remainder = totalCents - assigned;
  const balanced = remainder === 0;

  function setPart(index: number, value: number) {
    setParts((current) => current.map((part, i) => (i === index ? value : part)));
  }

  return (
    <BottomSheet label="Ventiler la dépense" onClose={onClose}>
      <p className="mt-1 text-xs text-ink-secondary">
        {formatEUR(totalCents)} à répartir entre {categories.length} catégories.
      </p>

      <div className="mt-4 flex flex-col gap-4">
        {categories.map((category, index) => (
          <AmountField
            key={category.id}
            label={`${category.icon} ${category.name}`}
            valueCents={parts[index] ?? 0}
            onChange={(value) => setPart(index, value)}
          />
        ))}
      </div>

      <div
        className={`mt-4 flex items-baseline justify-between rounded-xl border p-3 text-sm ${
          balanced ? 'border-line text-ink-secondary' : 'border-critical text-critical'
        }`}
      >
        <span>{balanced ? 'Réparti intégralement' : 'Reste à répartir'}</span>
        <span className="tabular font-medium">
          {balanced ? formatEUR(0) : formatSignedEUR(remainder)}
        </span>
      </div>

      {!balanced && (
        <button
          type="button"
          onClick={() => setParts(splitEvenly(totalCents, categories.length))}
          className="mt-2 w-full rounded-xl border border-line py-2.5 text-sm text-ink-secondary"
        >
          Répartir à parts égales
        </button>
      )}

      {error !== null && (
        <p role="alert" className="mt-2 text-sm text-critical">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-xl border border-line py-3 text-sm"
        >
          Annuler
        </button>
        <button
          type="button"
          disabled={pending || !balanced || parts.some((part) => part <= 0)}
          onClick={() => onConfirm(parts)}
          className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
        >
          Enregistrer
        </button>
      </div>
    </BottomSheet>
  );
}
